import { useMemo, useState } from "react";
import { Check, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Caixa, editarCaixa, registrarCaixa } from "services/caixas";
import { formatarNumero, pesoCubado, volumeLitros } from "utils/caixa";

type Props = {
  action: (caixa: Caixa) => void;
  returnPage: () => void;
  /**
   * Caixa em edicao. O nome antigo desta prop era `ref` — em React 19 `ref` e
   * uma prop de verdade em componente de funcao, entao o valor estava sendo
   * tratado tambem como referencia de elemento.
   */
  caixa?: Caixa;
};

/** Medidas de prateleira, para quem nao tem a caixa na mao na hora. */
const MODELOS = [
  { chave: "envelope", comprimento: 32, largura: 24, altura: 2 },
  { chave: "small", comprimento: 20, largura: 12, altura: 8 },
  { chave: "medium", comprimento: 30, largura: 20, altura: 15 },
  { chave: "large", comprimento: 40, largura: 30, altura: 25 },
];

/**
 * Aceita virgula decimal: o campo e texto, nao `type="number"`, porque em
 * pt-BR a pessoa digita "0,25" e o input numerico descarta o valor inteiro
 * quando encontra a virgula.
 */
const paraNumero = (texto: string) => {
  const limpo = texto.replace(",", ".").trim();
  if (!limpo) return 0;
  const n = Number(limpo);
  return Number.isFinite(n) ? n : 0;
};

const FormCaixa = ({ action, returnPage, caixa }: Props) => {
  const { t, i18n } = useTranslation("dashboard");
  const locale = i18n.language || "pt-BR";

  const [cod, setCod] = useState(caixa?.cod_identificacao ?? "");
  const [descricao, setDescricao] = useState(caixa?.descricao ?? "");
  const [comprimento, setComprimento] = useState(caixa?.comprimento ?? "");
  const [largura, setLargura] = useState(caixa?.largura ?? "");
  const [altura, setAltura] = useState(caixa?.altura ?? "");
  const [peso, setPeso] = useState(caixa?.peso ?? "");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const medidas = useMemo(
    () => ({
      c: paraNumero(String(comprimento)),
      l: paraNumero(String(largura)),
      a: paraNumero(String(altura)),
      p: paraNumero(String(peso)),
    }),
    [comprimento, largura, altura, peso],
  );

  const temMedidas = medidas.c > 0 && medidas.l > 0 && medidas.a > 0;
  const cubado = temMedidas ? pesoCubado(medidas.c, medidas.l, medidas.a) : 0;
  const volume = temMedidas ? volumeLitros(medidas.c, medidas.l, medidas.a) : 0;

  // O back exige todos positivos (isPosNumber em CaixaController), peso
  // incluso — validar aqui evita o 400 depois de preencher a tela toda.
  const podeSalvar =
    cod.trim() !== "" && descricao.trim() !== "" && temMedidas && medidas.p > 0;

  const aplicarModelo = (modelo: (typeof MODELOS)[number]) => {
    setComprimento(String(modelo.comprimento));
    setLargura(String(modelo.largura));
    setAltura(String(modelo.altura));
  };

  const salvar = () => {
    if (!podeSalvar || salvando) return;
    setSalvando(true);
    setErro(null);

    const payload = {
      cod_identificacao: cod.trim(),
      descricao: descricao.trim(),
      comprimento: medidas.c,
      largura: medidas.l,
      altura: medidas.a,
      peso: medidas.p,
    };

    const requisicao = caixa
      ? editarCaixa(caixa.id, payload)
      : registrarCaixa(payload);

    requisicao
      .then((data) => {
        action(data.caixa);
        returnPage();
      })
      .catch((e) => {
        // Antes o `.finally(returnPage)` voltava para a lista mesmo quando a
        // gravacao falhava: a caixa sumia da tela sem nunca ter sido salva.
        setErro(
          e?.response?.data?.erro ?? t("modal_boxes.save_error"),
        );
      })
      .finally(() => setSalvando(false));
  };

  return (
    <form
      className="flex flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        salvar();
      }}
    >
      <div className="grid grid-cols-1 md:grid-cols-[1fr_320px]">
        {/* CAMPOS */}
        <div className="space-y-5 px-6 py-5">
          <div className="grid grid-cols-2 gap-4">
            <Campo
              label={t("modal_boxes.field_code")}
              valor={cod}
              aoMudar={setCod}
              placeholder={t("modal_boxes.field_code_ph")}
              desabilitado={caixa !== undefined}
            />
            <Campo
              label={t("modal_boxes.field_name")}
              valor={descricao}
              aoMudar={setDescricao}
              placeholder={t("modal_boxes.field_name_ph")}
            />
          </div>

          <div>
            <p className="mb-2 text-xs font-medium text-slate-500">
              {t("modal_boxes.outer_size")}
            </p>
            <div className="grid grid-cols-3 gap-3">
              <Campo
                label={t("fields.lenght_short")}
                valor={String(comprimento)}
                aoMudar={setComprimento}
                sufixo="cm"
                inputMode="decimal"
              />
              <Campo
                label={t("fields.width_short")}
                valor={String(largura)}
                aoMudar={setLargura}
                sufixo="cm"
                inputMode="decimal"
              />
              <Campo
                label={t("fields.height_short")}
                valor={String(altura)}
                aoMudar={setAltura}
                sufixo="cm"
                inputMode="decimal"
              />
            </div>
            <p className="mt-2 text-[11px] text-slate-400">
              {t("modal_boxes.outer_size_hint")}
            </p>
          </div>

          <div>
            <p className="mb-2 text-xs font-medium text-slate-500">
              {t("modal_boxes.empty_weight")}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <div className="w-[180px]">
                <Campo
                  valor={String(peso)}
                  aoMudar={setPeso}
                  sufixo="kg"
                  inputMode="decimal"
                />
              </div>
              <p className="text-[11px] text-slate-400">
                {t("modal_boxes.empty_weight_hint")}
              </p>
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-medium text-slate-500">
              {t("modal_boxes.presets")}
            </p>
            <div className="flex flex-wrap gap-2">
              {MODELOS.map((modelo) => (
                <button
                  key={modelo.chave}
                  type="button"
                  onClick={() => aplicarModelo(modelo)}
                  className="rounded-full border border-slate-200 px-3 py-1.5 text-xs text-slate-600 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-brand-primary"
                >
                  {t(`modal_boxes.preset_${modelo.chave}`)} ·{" "}
                  {modelo.comprimento}×{modelo.largura}×{modelo.altura}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* PREVIA */}
        <aside className="border-slate-100 bg-slate-50/70 px-5 py-5 md:border-l">
          <p className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            {t("modal_boxes.preview")}
          </p>

          <div className="grid h-[150px] place-items-center rounded-xl border border-slate-200 bg-white">
            {temMedidas ? (
              <DesenhoCaixa
                comprimento={medidas.c}
                largura={medidas.l}
                altura={medidas.a}
              />
            ) : (
              <p className="px-4 text-center text-[11px] text-slate-400">
                {t("modal_boxes.preview_empty")}
              </p>
            )}
          </div>

          <dl className="mt-4 space-y-2 text-xs">
            <Linha
              termo={t("modal_boxes.volume")}
              valor={
                temMedidas ? `${formatarNumero(volume, 1, locale)} L` : "—"
              }
            />
            <Linha
              termo={t("modal_boxes.col_empty_weight")}
              valor={
                medidas.p > 0
                  ? `${formatarNumero(medidas.p, 2, locale)} kg`
                  : "—"
              }
            />
            <Linha
              termo={t("modal_boxes.col_cubed_weight")}
              valor={
                temMedidas ? `${formatarNumero(cubado, 1, locale)} kg` : "—"
              }
            />
          </dl>

          {temMedidas && medidas.p > 0 && cubado > medidas.p && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-[11px] leading-relaxed text-amber-800">
                {t("modal_boxes.cubed_warning")}
              </p>
            </div>
          )}
        </aside>
      </div>

      {/* RODAPE */}
      <div className="sticky bottom-0 flex items-center justify-between gap-4 border-t border-slate-100 bg-white px-6 py-4">
        <button
          type="button"
          onClick={returnPage}
          className="text-sm text-slate-500 transition-colors hover:text-slate-700"
        >
          {t("buttons.cancel")}
        </button>

        <div className="flex items-center gap-3">
          {erro && (
            <span className="flex items-center gap-1.5 text-xs text-red-600">
              <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
              {erro}
            </span>
          )}
          <button
            type="submit"
            disabled={!podeSalvar || salvando}
            className="flex h-10 items-center gap-2 rounded-lg bg-brand-sidebar px-5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Check className="h-4 w-4" />
            {salvando ? t("modal_boxes.saving") : t("modal_boxes.save")}
          </button>
        </div>
      </div>
    </form>
  );
};

export default FormCaixa;

type CampoProps = {
  label?: string;
  valor: string;
  aoMudar: (v: string) => void;
  placeholder?: string;
  sufixo?: string;
  desabilitado?: boolean;
  inputMode?: "text" | "decimal";
};

const Campo = ({
  label,
  valor,
  aoMudar,
  placeholder,
  sufixo,
  desabilitado,
  inputMode = "text",
}: CampoProps) => (
  <label className="block">
    {label && (
      <span className="mb-1.5 block text-[11px] font-medium text-slate-500">
        {label}
      </span>
    )}
    <div className="relative">
      <input
        type="text"
        inputMode={inputMode}
        value={valor}
        disabled={desabilitado}
        placeholder={placeholder}
        onChange={(e) => aoMudar(e.target.value)}
        className={`h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition-colors placeholder:text-slate-300 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary disabled:bg-slate-50 disabled:text-slate-400 ${
          sufixo ? "pr-10" : ""
        }`}
      />
      {sufixo && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
          {sufixo}
        </span>
      )}
    </div>
  </label>
);

const Linha = ({ termo, valor }: { termo: string; valor: string }) => (
  <div className="flex items-center justify-between">
    <dt className="text-slate-500">{termo}</dt>
    <dd className="font-semibold text-slate-800">{valor}</dd>
  </div>
);

/**
 * Caixa em projecao isometrica, proporcional as medidas digitadas — um
 * envelope 32×24×2 fica chato, um cubo fica cubo. O desenho e normalizado pelo
 * proprio contorno, entao qualquer proporcao cabe no quadro sem sobrar nem
 * estourar.
 */
const DesenhoCaixa = ({
  comprimento,
  largura,
  altura,
}: {
  comprimento: number;
  largura: number;
  altura: number;
}) => {
  const LARGURA_QUADRO = 170;
  const ALTURA_QUADRO = 118;

  // Eixos da projecao: largura desce para a direita, comprimento sobe para a
  // direita (profundidade), altura sobe na vertical.
  const ponto = (ul: number, uc: number, ua: number) => ({
    x: ul * largura * 1 + uc * comprimento * 0.92,
    y: ul * largura * 0.5 - uc * comprimento * 0.5 - ua * altura,
  });

  const v = {
    frenteBaixoEsq: ponto(0, 0, 0),
    frenteBaixoDir: ponto(1, 0, 0),
    frenteTopoDir: ponto(1, 0, 1),
    frenteTopoEsq: ponto(0, 0, 1),
    fundoTopoEsq: ponto(0, 1, 1),
    fundoTopoDir: ponto(1, 1, 1),
    fundoBaixoDir: ponto(1, 1, 0),
  };

  const todos = Object.values(v);
  const minX = Math.min(...todos.map((p) => p.x));
  const maxX = Math.max(...todos.map((p) => p.x));
  const minY = Math.min(...todos.map((p) => p.y));
  const maxY = Math.max(...todos.map((p) => p.y));

  const escala = Math.min(
    (LARGURA_QUADRO - 24) / Math.max(maxX - minX, 0.001),
    (ALTURA_QUADRO - 24) / Math.max(maxY - minY, 0.001),
  );

  const proj = (p: { x: number; y: number }) =>
    `${(p.x - minX) * escala + (LARGURA_QUADRO - (maxX - minX) * escala) / 2},${
      (p.y - minY) * escala + (ALTURA_QUADRO - (maxY - minY) * escala) / 2
    }`;

  const face = (pontos: { x: number; y: number }[]) =>
    pontos.map(proj).join(" ");

  return (
    <svg
      width={LARGURA_QUADRO}
      height={ALTURA_QUADRO}
      viewBox={`0 0 ${LARGURA_QUADRO} ${ALTURA_QUADRO}`}
      aria-hidden="true"
    >
      {/* topo */}
      <polygon
        points={face([
          v.frenteTopoEsq,
          v.frenteTopoDir,
          v.fundoTopoDir,
          v.fundoTopoEsq,
        ])}
        fill="#C7D2FE"
        stroke="#818CF8"
        strokeWidth="1"
        strokeLinejoin="round"
      />
      {/* frente */}
      <polygon
        points={face([
          v.frenteBaixoEsq,
          v.frenteBaixoDir,
          v.frenteTopoDir,
          v.frenteTopoEsq,
        ])}
        fill="#A5B4FC"
        stroke="#818CF8"
        strokeWidth="1"
        strokeLinejoin="round"
      />
      {/* lateral direita */}
      <polygon
        points={face([
          v.frenteBaixoDir,
          v.fundoBaixoDir,
          v.fundoTopoDir,
          v.frenteTopoDir,
        ])}
        fill="#8B9CF9"
        stroke="#818CF8"
        strokeWidth="1"
        strokeLinejoin="round"
      />
    </svg>
  );
};
